import { fetchAPI } from './apiClient';

export interface GanadorInfo {
  num1: string;
  num2: string | null;
  num3: string | null;
  tipo_premio: string | null;
  tipo_monazos: string | null;
  paga_reventado: boolean | null;
}

export interface PremioCombo {
  combinacion: string;
  monto: string;
  premio_cubierto_banquero?: string;
  premio_fuera_cobertura?: string;
}

export interface TicketPremiadoItem {
  ticket_number: string;
  vendedor_id: string | null;
  monto_apostado: string;
  premio_pagado: string;
  premio_cubierto_banquero?: string;
  premio_fuera_cobertura?: string;
}

export interface DetalleRecorteLista {
  numero: string;
  tipo_juego: string | null;
  monto_original: string;
  monto_recorte: string;
  monto_cubierto: string;
  recortes_aplicados: string[];
}

export interface ReporteSorteo {
  sorteo_id: string;
  sorteo_nombre: string;
  tipo_id: string;
  estado: string;
  total_vendido: string;
  total_premios: string;
  venta_real?: string;
  monto_recortado_lista?: string;
  venta_cubierta_banquero?: string;
  incluye_recortes_lista?: boolean;
  advertencia_recortes_lista?: string | null;
  detalle_recortes_lista?: DetalleRecorteLista[];
  comision_porcentaje: string | null;
  comision_potencial: string;
  comision_ganada: string;
  comision_no_ganada: string;
  comision_no_ganada_por_recorte?: string;
  comision_no_ganada_motivo: string;
  resultado_informativo: string;
  premio_total_real?: string;
  premios_cubiertos_banquero?: string;
  premios_fuera_cobertura?: string;
  responsabilidad_banca_recorte?: string;
  usa_restringidos: boolean;
  total_vendido_restringidos: string | null;
  total_vendido_no_restringidos: string | null;
  detalle_restringidos: DetalleRestringido[] | null;
  riesgo_pct: string | null;
  ganadores: GanadorInfo[];
  premios_por_combo: PremioCombo[];
  total_premios_pagados: string;
  tickets_premiados: TicketPremiadoItem[];
  /** Solo presente cuando se solicita modo_premios=totales (informativo). */
  premios_totales?: string;
}

export interface DetalleRestringido {
  numero: string;
  monto_vendido: string;
  monto_permitido: string;
  riesgo_pct: string;
  comision_ganada: string;
  comision_perdida: string;
}

export interface ReporteTotales {
  total_vendido: string;
  total_premios: string;
  total_premios_pagados: string;
  total_comision_ganada: string;
  total_comision_no_ganada: string;
  total_monto_recortado_lista?: string;
  total_venta_cubierta_banquero?: string;
  total_comision_no_ganada_por_recorte?: string;
  total_premios_cubiertos_banquero?: string;
  total_premios_fuera_cobertura?: string;
  total_responsabilidad_banca_recorte?: string;
  incluye_recortes_lista?: boolean;
  total_pagas: string;
  /** Solo presente cuando se solicita modo_premios=totales (informativo). */
  total_premios_totales?: string;
}

export interface ReporteResponse {
  fecha: string;
  sorteos: ReporteSorteo[];
  totales: ReporteTotales;
}

export interface LocalOption {
  id: string;
  nombre: string;
}

export interface SorteoDelDia {
  id: string;
  nombre: string;
  tipo_id: string;
  estado: string;
  hora_cierre: string | null;
}

export async function fetchReportePorSorteo(
  fecha: string,
  sorteoId?: string,
  localId?: string,
  fechaFin?: string,
  modoPremios?: 'cambiados' | 'totales',
): Promise<ReporteResponse> {
  const params = new URLSearchParams();
  if (fechaFin) {
    params.set('fecha_inicio', fecha);
    params.set('fecha_fin', fechaFin);
  } else {
    params.set('fecha', fecha);
  }
  if (sorteoId) params.append('sorteo_id', sorteoId);
  if (localId) params.append('local_id', localId);
  // Solo se propaga el query si está definido — sin él, la llamada es
  // idéntica a la versión histórica (regresión protegida en backend).
  if (modoPremios) params.append('modo_premios', modoPremios);
  return fetchAPI<ReporteResponse>(`/reportes/por-sorteo?${params.toString()}`);
}

export async function fetchLocalesReporte(): Promise<LocalOption[]> {
  return fetchAPI<LocalOption[]>('/reportes/locales');
}

export async function fetchSorteosDelDia(fecha: string, fechaFin?: string): Promise<SorteoDelDia[]> {
  const params = new URLSearchParams();
  if (fechaFin) {
    params.set('fecha_inicio', fecha);
    params.set('fecha_fin', fechaFin);
  } else {
    params.set('fecha', fecha);
  }
  return fetchAPI<SorteoDelDia[]>(`/reportes/sorteos-del-dia?${params.toString()}`);
}

export async function fetchPremiosPagados(
  fecha: string,
  sorteoId?: string,
  localId?: string,
): Promise<any[]> {
  const params = new URLSearchParams({ fecha });
  if (sorteoId) params.append('sorteo_id', sorteoId);
  if (localId) params.append('local_id', localId);
  return fetchAPI<any[]>(`/reportes/premios?${params.toString()}`);
}
